import React, { useMemo, useState, useEffect } from "react";
import {
  Button, Checkbox, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, useTheme
} from "@mui/material";
import { CheckStatus, CheckStatusLabel } from "../../../../../utils/enums";
import ReusableTable from "../../../../../components/ReusableTable/ReusableTable";
import { useToday, formatDate, formatDateTime } from "../../../../../utils/dateUtils";
import ConfirmSnackbar from "../../../../../components/Snackbar/ConfirmSnackbar";
import { CheckStatusChip } from "../../../../../components/CheckStatusChip/CheckStatusChip";
import { buttonBaseStyles, inputBaseStyles } from "./sharedStyles";
import { DeleteExceptionDialog } from "../../../../Settings/Frameworks/FrameWorkDialogs/CheckDetails/ExceptionComponents/DeleteExceptionDialog";
import { ExceptionDetailsForm } from "../../../../Settings/Frameworks/FrameWorkDialogs/CheckDetails/ExceptionComponents/ExceptionDetailsForm";
import ResourceSelectCell from "../../common/ResourceSelectCell";
import ResourceStatusCell from "../../common/ResourceStatusCell";
import ResourceReasonCell from "../../common/ResourceReasonCell";
import ResourceNameCell from "../../common/ResourceNameCell";
import DefaultCell from "../../common/DefaultCell";
import ResourcesTableContainer from "../../common/ResourcesTableContainer";
import BulkDeleteExceptionDialog from "../../common/BulkDeleteExceptionDialog";
import ExceptionActionMenu from "../../common/ExceptionActionMenu";
import RemediationDrawer from "../../common/RemediationDrawer";
import { Badge as BadgeIcon } from "@mui/icons-material";
import CheckNotRunAlert from "../../common/CheckNotRunAlert";
import ReusableTableSkeleton from "../../../../../components/ReusableTable/TableSkeleton";

const InfraResourcesTabBase = ({
  providerName,
  activeCheckId,
  onRefetch,
  onStartPolling,
  resourcesData,
  resourcesLoading,
  exceptionsData,
  refetchExceptions,
  refetchResources,
  createException,
  creatingException,
  deleteException,
  mapResource,
  columns,
  getAccountId,
  globalException,
  isTriggerDisabled,
  checkStatus,
  // Opt-in gate from the recipe's `remediation_enabled`. False for a check
  // whose bindings and fetchers have not been verified against real rows, and
  // the Remediate button is then not rendered at all -- offering a fix built
  // from unverified prefills is worse than offering no fix.
  remediationEnabled = false,
  isPollingRunStatus = false,
  selectedDate,
  onDateChange,
}) => {
  const theme = useTheme();
  const today = useToday();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedResources, setSelectedResources] = useState([]);
  const [remediationOpen, setRemediationOpen] = useState(false);
  const [createExceptionOpen, setCreateExceptionOpen] = useState(false);
  const [exceptionFormData, setExceptionFormData] = useState({ reason: "", validUntil: "" });
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [exceptionToDelete, setExceptionToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [tableExpanded, setTableExpanded] = useState(true);
  const [tableFilters, setTableFilters] = useState({});

  const isValidUntilPast = exceptionFormData.validUntil && exceptionFormData.validUntil < today;

  // Refetch exceptions and resources when global exception changes
  useEffect(() => {
    if (refetchExceptions) {
      refetchExceptions();
    }
    if (refetchResources) {
      refetchResources();
    }
  }, [globalException?.id, refetchExceptions, refetchResources]);

  const resources = useMemo(() => {
    if (!resourcesData) return [];
    const exceptions = exceptionsData?.data || [];
    return mapResource(resourcesData, exceptions);
  }, [resourcesData, exceptionsData, mapResource]);

  const normalizeStatus = (status = "") => {
    const normalized = status?.toLowerCase() || "";
    if (normalized.includes(CheckStatus.PASS)) return CheckStatus.PASS;
    if (normalized.includes(CheckStatus.FAIL)) return CheckStatus.FAIL;
    if (normalized.includes(CheckStatus.DISABLED)) return CheckStatus.DISABLED;
    return CheckStatus.PENDING;
  };

  // Remediation always covers every failing resource, so it is driven by the
  // check's own state rather than the table selection. Passing resources are
  // excluded here and again server-side.
  const failingResourceCount = useMemo(
    () =>
      (resources || []).filter(
        (res) => normalizeStatus(res?.status) === CheckStatus.FAIL
      ).length,
    [resources]
  );

  const filteredResources = useMemo(() => {
    if (!resources) return [];
    let filtered = resources;
    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter((res) =>
        res?.name?.toLowerCase().includes(query) ||
        (res?.resourceId && res.resourceId.toString().toLowerCase().includes(query)) ||
        (res?.account && res.account?.toLowerCase().includes(query)) ||
        (res?.subscription && res.subscription?.toLowerCase().includes(query)) ||
        (res?.project && res.project?.toLowerCase().includes(query))
      );
    }
    const statusFilters = tableFilters?.status || [];
    if (statusFilters.length > 0) {
      filtered = filtered.filter((res) => statusFilters.includes(normalizeStatus(res?.status)));
    }

    Object.entries(tableFilters || {}).forEach(([field, values]) => {
      if (field === "status" || !values || values.length === 0) return;
      filtered = filtered.filter((res) => {
        const value = res?.[field];
        const normalizedValue = value === null || value === undefined ? "" : String(value);
        return values.includes(normalizedValue);
      });
    });

    return filtered;
  }, [resources, searchTerm, tableFilters]);

  const clearFilters = () => {
    setSearchTerm("");
    setTableFilters({});
  };

  const handleTableFilterChange = (filters) => {
    setTableFilters(filters || {});
  };

  const statusFilterOptions = useMemo(() => ([
    { id: CheckStatus.PASS, label: CheckStatusLabel.PASSING },
    { id: CheckStatus.FAIL, label: CheckStatusLabel.FAILING },
    { id: CheckStatus.DISABLED, label: CheckStatusLabel.DISABLED },
    { id: CheckStatus.PENDING, label: CheckStatusLabel.PENDING },
  ]), []);

  const handleSelectResource = (resourceId) => {
    setSelectedResources((prev) => prev.includes(resourceId) ? prev.filter((id) => id !== resourceId) : [...prev, resourceId]);
  };

  const handleSelectAll = () => {
    setSelectedResources(selectedResources.length === filteredResources.length ? [] : filteredResources.map((res) => res.selectionId));
  };

  const handleOpenCreateException = (resourceId = null) => {
    if (resourceId) {
      setSelectedResources([resourceId]);
    }
    setCreateExceptionOpen(true);
  };

  const handleCloseCreateException = () => {
    setCreateExceptionOpen(false);
    setExceptionFormData({ reason: "", validUntil: "" });
  };

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteException({
        variables: { ids: [Number(exceptionToDelete?.id)] },
      });
      setDeleteDialogOpen(false);
      setExceptionToDelete(null);
      setSnackbarInfo({
        message: "Exception deleted successfully!",
        vertical: "top",
        horizontal: "right",
      });
      if (refetchExceptions) {
        await refetchExceptions();
      }
      if (refetchResources) {
        await refetchResources();
      }
      if (onRefetch) {
        await onRefetch();
      }
      if (onStartPolling) {
        onStartPolling();
      }
    } catch (error) {
      setSnackbarInfo({
        message: error?.message || "Failed to delete exception.",
        vertical: "top",
        horizontal: "right",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const selectedExceptionRows = useMemo(() => {
    if (!selectedResources?.length) return [];
    const bySelectionId = new Map(
      resources.map((res) => [res.selectionId, res])
    );
    return selectedResources
      .map((sid) => bySelectionId.get(sid))
      .filter((res) => res && res.exceptionId != null && res.exceptionData);
  }, [selectedResources, resources]);

  const handleConfirmBulkDelete = async () => {
    const ids = selectedExceptionRows
      .map((res) => Number(res.exceptionData?.id))
      .filter((id) => !Number.isNaN(id));
    if (!ids.length) return;
    setDeleteLoading(true);
    try {
      await deleteException({ variables: { ids } });
      setBulkDeleteOpen(false);
      setSelectedResources([]);
      setSnackbarInfo({
        message: `${ids.length} exception${ids.length > 1 ? "s" : ""} deleted successfully!`,
        vertical: "top",
        horizontal: "right",
      });
      if (refetchExceptions) await refetchExceptions();
      if (refetchResources) await refetchResources();
      if (onRefetch) await onRefetch();
      if (onStartPolling) onStartPolling();
    } catch (error) {
      setSnackbarInfo({
        message: error?.message || "Failed to delete exceptions.",
        vertical: "top",
        horizontal: "right",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCreateException = async () => {
    if (!exceptionFormData.reason.trim()) {
      setSnackbarInfo({ message: "Please provide justification", vertical: "top", horizontal: "right" });
      return;
    }
    if (!exceptionFormData.validUntil) {
      setSnackbarInfo({ message: "Please provide expiration date", vertical: "top", horizontal: "right" });
      return;
    }
    if (exceptionFormData.validUntil < today) {
      setSnackbarInfo({ message: "Expiration date cannot be in the past", vertical: "top", horizontal: "right" });
      return;
    }

    try {
      const resourcesPayload = selectedResources.map((selectionId) => {
        const resource = resources.find((r) => r.selectionId === selectionId);
        const accountId = getAccountId ? getAccountId(resource) : undefined;
        const item = {
          resource_id: String(resource.resourceId),
        };
        if (accountId) {
          item.account_id = String(accountId);
        } else if (getAccountId) {
          throw new Error(`Account ID is required for resource exceptions. Resource not found or missing account information.`);
        }
        return item;
      });

      const result = await createException({
        variables: {
          exception_data: {
            active_check_id: Number(activeCheckId),
            scope: "resource",
            reason: exceptionFormData.reason,
            exception_valid_till: exceptionFormData.validUntil,
            resources: resourcesPayload,
          },
        },
      });

      const successKey = `create${providerName}CheckException`;
      const allSuccessful = !!result?.data?.[successKey]?.success;

      if (allSuccessful) {
        setSnackbarInfo({ message: `Exception created for ${selectedResources.length} resource${selectedResources.length > 1 ? 's' : ''}!`, vertical: "top", horizontal: "right" });
        setSelectedResources([]);
        handleCloseCreateException();
        await refetchExceptions();
        if (refetchResources) {
          await refetchResources();
        }
        if (onRefetch) await onRefetch();
        if (onStartPolling) onStartPolling();
      } else {
        const errorMessage = result?.data?.[successKey]?.message || "Failed to create exception";
        setSnackbarInfo({ message: errorMessage, vertical: "top", horizontal: "right" });
      }
    } catch (error) {
      setSnackbarInfo({ message: error?.message || "Failed to create exception", vertical: "top", horizontal: "right" });
    }
  };


  const resourceColumns = [
    {
      label: (
        <Checkbox
          checked={selectedResources.length === filteredResources.length && filteredResources.length > 0}
          indeterminate={selectedResources.length > 0 && selectedResources.length < filteredResources.length}
          onChange={handleSelectAll}
          disabled={!!globalException}
          sx={{ color: "#031B89", "&.Mui-checked": { color: "#031B89" }, "&.MuiCheckbox-indeterminate": { color: "#031B89" } }}
        />
      ),
      field: "select",
      width: "7%",
      align: "center"
    },
    ...columns,
    {
      label: "Status",
      field: "status",
      align: "center",
      width: "12%",
      filterConfig: {
        filterOptions: statusFilterOptions,
      },
    },
    { label: "Reason", field: "reason", align: "left", width: "40%" },
  ];

  const renderResourceCell = (row, column) => {
    if (column?.field === "select") {
      return (
        <ResourceSelectCell
          resourceId={row.selectionId}
          selectedResources={selectedResources}
          onSelect={handleSelectResource}
          disabled={!!globalException}
        />
      );
    }
    if (column?.field === "status") {
      return (
        <ResourceStatusCell
          status={row.status}
          exceptionValidUntil={row.exceptionValidUntil}
          isRefreshing={isPollingRunStatus}
        />
      );
    }
    if (column?.field === "reason") {
      const hasException = row?.exceptionId && row?.exceptionData;
      const isResourceSelected = selectedResources.includes(row.selectionId);
      const isBulkMode = selectedResources.length >= 1;
      const isDeleteDisabled =
        !hasException ||
        (isBulkMode && isResourceSelected) ||
        !!globalException ||
        isPollingRunStatus;

      return (
        <ResourceReasonCell
          reason={row.reason}
          hasException={hasException}
          onDelete={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (!isDeleteDisabled) {
              setExceptionToDelete(row.exceptionData);
              setDeleteDialogOpen(true);
            }
          }}
          disabled={isDeleteDisabled}
          hasGlobalException={!!globalException}
          isBulkMode={isBulkMode}
          isResourceSelected={isResourceSelected}
        />
      );
    }
    if (column?.field === "name" || column?.field === "resourceId") {
      return <ResourceNameCell value={row[column?.field]} />;
    }
    if (column?.field === "policy_execution_time") {
      return (
        <DefaultCell
          value={formatDateTime(row.policy_execution_time) || "N/A"}
        />
      );
    }
    return <DefaultCell value={row[column?.field]} />;
  };

  const hasAnyResources = resources && resources.length > 0;
  if (!resourcesLoading && !hasAnyResources && !selectedDate) {
    if (!isTriggerDisabled && checkStatus === CheckStatus.PENDING) {
      return <CheckNotRunAlert />;
    }
    return null;
  }

  return (
    <>
      <ResourcesTableContainer
        title="Resources"
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        clearFilters={clearFilters}
        filteredCount={filteredResources.length}
        tableExpanded={tableExpanded}
        onToggleExpand={() => setTableExpanded(!tableExpanded)}
        selectedCount={selectedResources.length}
        selectedLabel="Selected"
        remediateButton={
          remediationEnabled && (
          <Tooltip
            title={
              failingResourceCount === 0
                ? "Nothing to remediate — no failing resources"
                : `Show fix commands for all ${failingResourceCount} failing resource${
                    failingResourceCount > 1 ? "s" : ""
                  }`
            }
            arrow
            placement="top"
          >
            <span>
              {/* Styled to match ExceptionActionMenu -- the two sit side by
                  side and read as one control group. */}
              <Button
                variant="contained"
                disabled={failingResourceCount === 0}
                onClick={() => setRemediationOpen(true)}
                sx={{
                  bgcolor: theme.customColors.button.primary,
                  color: theme.customColors.button.text,
                  fontWeight: 500,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "14px",
                  px: 1,
                  py: 1,
                  minWidth: 120,
                  whiteSpace: "nowrap",
                  "&:hover": { bgcolor: theme.palette._blue.dark },
                  "&:disabled": {
                    bgcolor: theme.customColors.background.light,
                    color: theme.customColors.text.disabled,
                  },
                }}
              >
                Remediate
              </Button>
            </span>
          </Tooltip>
          )
        }
        markExceptionButton={
          !globalException && (
            <ExceptionActionMenu
              disabled={
                selectedResources.length === 0 ||
                creatingException ||
                isPollingRunStatus
              }
              disabledTooltip={
                isPollingRunStatus
                  ? "Please wait for the check to finish updating"
                  : selectedResources.length === 0
                  ? "Select at least one resource to manage exceptions"
                  : ""
              }
              onMark={() => handleOpenCreateException()}
              markDisabled={creatingException}
              markDescription="Create exception for selected resources"
              onDelete={() => setBulkDeleteOpen(true)}
              deleteDisabled={
                selectedExceptionRows.length === 0 ||
                deleteLoading ||
                isPollingRunStatus
              }
              deleteCount={selectedExceptionRows.length}
              deleteDescription={
                selectedExceptionRows.length === 0
                  ? "No selected resources have an existing exception"
                  : "Remove existing exception(s)"
              }
            />
          )
        }
      >
        {resourcesLoading ? (
          <ReusableTableSkeleton columns={resourceColumns} skeletonRows={6} />
        ) : (
          <ReusableTable
            columns={resourceColumns}
            data={filteredResources}
            renderCell={renderResourceCell}
            showPagination={true}
            height="auto"
            rowsPerPageOptions={[50, 100]}
            filters={tableFilters}
            onFilterChange={handleTableFilterChange}
          />
        )}
      </ResourcesTableContainer>

      {/* Delete Exception Dialog */}
      {deleteDialogOpen && exceptionToDelete && (
        <DeleteExceptionDialog
          deleteDialogOpen={deleteDialogOpen}
          setDeleteDialogOpen={setDeleteDialogOpen}
          exceptionToDelete={exceptionToDelete}
          submitLoading={deleteLoading}
          handleConfirmDelete={handleConfirmDelete}
        />
      )}

      <BulkDeleteExceptionDialog
        open={bulkDeleteOpen}
        onCancel={() => setBulkDeleteOpen(false)}
        onConfirm={handleConfirmBulkDelete}
        loading={deleteLoading}
        count={selectedExceptionRows.length}
        previewItems={selectedExceptionRows.map((res) => ({
          label:
            res.name || res.resourceId || res.exceptionData?.resource_id,
          reason: res.exceptionData?.reason,
          validUntil: res.exceptionData?.exception_valid_till,
        }))}
        entityLabel="exception"
        entityIcon={BadgeIcon}
      />

      {/* Create Exception Dialog */}
      <Dialog
        open={createExceptionOpen}
        onClose={handleCloseCreateException}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            minWidth: 600,
            maxWidth: 700,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: "1.25rem",
            color: theme.customColors.text.strong,
            background: theme.customColors.background.cardHeader,
            borderBottom: `1px solid ${theme.customColors.border.main}`,
            pb: 2,
            pt: 2,
            px: 3,
          }}
        >
          Create Exception for {selectedResources.length} Resource{selectedResources.length !== 1 ? 's' : ''}
        </DialogTitle>
        <DialogContent
          sx={{
            px: 3,
            pt: 3,
            pb: 0,
            "&.MuiDialogContent-root": {
              paddingTop: 3,
            },
          }}
        >
          <ExceptionDetailsForm
            reason={exceptionFormData.reason}
            setReason={(val) => setExceptionFormData({ ...exceptionFormData, reason: val })}
            validUntil={exceptionFormData.validUntil}
            setValidUntil={(val) => setExceptionFormData({ ...exceptionFormData, validUntil: val })}
            isValidUntilPast={isValidUntilPast}
          />
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            px: 3,
            pb: 2,
            pt: 2,
            borderTop: `1px solid ${theme.customColors.border.subtle}`,
            mt: 1,
          }}
        >
          <Button
            onClick={handleCloseCreateException}
            disabled={creatingException}
            variant="outlined"
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: "0.8125rem",
              px: 2,
              py: 0.75,
              borderRadius: 1,
              borderColor: theme.customColors.border.main,
              color: theme.customColors.text.strong,
              "&:hover": {
                borderColor: theme.customColors.border.strong,
                backgroundColor: theme.customColors.background.hover,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateException}
            variant="contained"
            disabled={!exceptionFormData.reason?.trim() || !exceptionFormData.validUntil || isValidUntilPast || creatingException}
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: "0.8125rem",
              px: 2,
              py: 0.75,
              borderRadius: 1,
              bgcolor: theme.customColors.button.primary,
              color: theme.customColors.button.text,
              "&:hover": {
                bgcolor: theme.palette._blue.dark,
              },
              "&:disabled": {
                bgcolor: theme.customColors.background.light,
                color: theme.customColors.text.disabled,
              },
            }}
          >
            {creatingException ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <RemediationDrawer
        open={remediationOpen}
        onClose={() => setRemediationOpen(false)}
        checkId={Number(activeCheckId)}
      />

      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </>
  );
};

export default InfraResourcesTabBase;

